/**
 * Checkout entry for the shared TRX PayPage Fields script loader.
 *
 * The loader itself (script-tag injection, once-per-tab init guard,
 * retry-on-failed-load cache) lives in `@order-up/common`'s
 * `useTrxPayPageScript` — env-var sourcing is the only thing that ever
 * differed between the app copies, so this file just resolves the Vite
 * env var (`VITE_TRX_PAYPAGE_FIELDS_URL` via `__VITE_ENV__`; UAT bundle
 * when unset — production builds MUST override to the `.com` host) and
 * widens the shared ACH-slice `PayPageJS` type to the full card + wallet
 * surface `TrxJsLibraryInput` consumes (`../types/trxPayPage.ts`).
 */

import { useTrxPayPageScript as useSharedTrxPayPageScript } from '@order-up/common';
import type { TrxPayPageGlobal } from '../types/trxPayPage';

export interface UseTrxPayPageScriptResult {
    readonly ready: boolean;
    readonly error: Error | null;
    readonly PayPageJS: TrxPayPageGlobal | null;
}

export function useTrxPayPageScript(): UseTrxPayPageScriptResult {
    const { ready, error, PayPageJS } = useSharedTrxPayPageScript(
        __VITE_ENV__.TRX_PAYPAGE_FIELDS_URL,
    );
    // The shared hook types the global as the ACH slice `@order-up/common`
    // owns; the same `window.PayPageJS` object also carries the card and
    // wallet surface declared in `../types/trxPayPage.ts`. The widening is
    // safe because both declarations describe the one runtime object (§11
    // of `docs/TRX_API_REFERENCE.md`).
    return { ready, error, PayPageJS: PayPageJS as TrxPayPageGlobal | null };
}
